import type { IconName } from "~/components/SvgIconRaw";
import type { FileRouteTypes } from "~/routeTree.gen";

import { RouteStaticData } from "./Types";
import { ROUTE_STATIC } from "./routeStaticData";

/**
 * Presentation half of the feature registry: where each registry id renders
 * and how it looks. Whether it shows is the access payload's answer, never
 * this file's. Ids are checked against the committed `/features` snapshot.
 */
export interface NavEntry {
  /** Registry id of a `nav` feature. */
  id: string;
  to: FileRouteTypes["to"];
  staticData: RouteStaticData;
  icon: IconName;
  /** Key under `common:sectionNames`. */
  sectionName: string;
}

// Order matters: the sidebar renders in this order and /main lands on the
// first entry the selected role can see.
export const NAV_MANIFEST: NavEntry[] = [
  {
    id: "administration",
    to: "/administration",
    staticData: ROUTE_STATIC.administration,
    icon: "Tool",
    sectionName: "Administration",
  },
  {
    id: "meq-live-admin",
    to: "/meq-live-admin",
    staticData: ROUTE_STATIC.meqLiveAdmin,
    icon: "Scan",
    sectionName: "MEQ Live Admin",
  },
  {
    id: "gmp-lamb-data-analysis",
    to: "/gmp-lamb-data-analysis",
    staticData: ROUTE_STATIC.gmpLambDataAnalysis,
    icon: "BarChartSquare",
    sectionName: "GMP Lamb Data Analysis",
  },
  {
    id: "gmp-lamb-bookings",
    to: "/gmp-lamb-bookings",
    staticData: ROUTE_STATIC.gmpLambBookings,
    icon: "ClipboardMinus",
    sectionName: "GMP Lamb Bookings",
  },
  {
    id: "gmp-upload",
    to: "/gmp-upload",
    staticData: ROUTE_STATIC.gmpUpload,
    icon: "FilePlus02",
    sectionName: "GMP Upload",
  },
  {
    id: "data-analysis-overview",
    to: "/data-analysis-overview",
    staticData: ROUTE_STATIC.dataAnalysisOverview,
    icon: "BarChartSquare",
    sectionName: "Data Analysis Overview",
  },
  {
    id: "meq-live",
    to: "/meq-live",
    staticData: ROUTE_STATIC.meqLive,
    icon: "Scan",
    sectionName: "MEQ Live",
  },
  {
    id: "meq-probe",
    to: "/meq-probe",
    staticData: ROUTE_STATIC.meqProbe,
    icon: "LocationMark",
    sectionName: "MEQ Probe",
  },
  {
    id: "meq-camera",
    to: "/meq-camera",
    staticData: ROUTE_STATIC.meqCamera,
    icon: "Camera",
    sectionName: "MEQ Camera",
  },
  {
    id: "meq-live-genetics",
    to: "/meq-live-genetics",
    staticData: ROUTE_STATIC.meqLiveGenetics,
    icon: "BarChartSquare",
    sectionName: "MEQ Live Genetics",
  },
  {
    id: "usda-audit",
    to: "/usda-audit",
    staticData: ROUTE_STATIC.usdaAudit,
    icon: "ImageIndentRight",
    sectionName: "USDA Audit",
  },
  {
    id: "usda-audit-feedback",
    to: "/usda-audit-feedback",
    staticData: ROUTE_STATIC.usdaAuditFeedback,
    icon: "ImageIndentRight",
    sectionName: "USDA Audit Feedback",
  },
  {
    id: "record-labelling-admin",
    to: "/record-labelling-admin",
    staticData: ROUTE_STATIC.recordLabellingAdmin,
    icon: "ClipboardMinus",
    sectionName: "Record Labelling Admin",
  },
  {
    id: "my-assignments",
    to: "/record-labelling/my-assignments",
    staticData: ROUTE_STATIC.myAssignments,
    icon: "CheckCircle",
    sectionName: "Record Labelling",
  },
  {
    id: "usda-image-calibration",
    to: "/usda-image-calibration",
    staticData: ROUTE_STATIC.usdaImageCalibration,
    icon: "Image",
    sectionName: "USDA Image Calibration",
  },
  {
    id: "management",
    to: "/management",
    staticData: ROUTE_STATIC.management,
    icon: "Users",
    sectionName: "Management",
  },
];

export interface TabEntry {
  /** Registry id of a `tab` feature, or of the nav itself for a root tab. */
  id: string;
  /** Path under the section's route; "/" is the section route itself. */
  path: string;
}

/**
 * Tabs per section, in render order. Two entries may share a path when the
 * lens swaps the page rendered there; the payload never shows both.
 */
export const TAB_MANIFEST = {
  administration: [
    { id: "administration.organisations", path: "/" },
    { id: "administration.org-locations", path: "/locations" },
    { id: "administration.users", path: "/users" },
    { id: "administration.action-log", path: "/action-log" },
    { id: "administration.audit-log", path: "/audit" },
  ],
  "gmp-lamb-data-analysis": [
    { id: "gmp.dashboard", path: "/" },
    { id: "gmp.awards", path: "/awards" },
    { id: "gmp.comparison", path: "/comparison" },
    { id: "gmp.animal-health", path: "/animal-health" },
    { id: "gmp.producer-dashboard", path: "/" },
  ],
  "gmp-lamb-bookings": [
    { id: "gmp-bookings.bookings", path: "/" },
    { id: "gmp-bookings.weekly-overview", path: "/weekly-overview" },
    { id: "gmp-bookings.admin", path: "/admin" },
    { id: "gmp-bookings.producer", path: "/" },
  ],
  "data-analysis-overview": [
    { id: "dao.beef-overview", path: "/" },
    { id: "dao.producers", path: "/producers" },
    { id: "dao.comparisons", path: "/comparisons" },
    { id: "dao.live-to-camera", path: "/live-to-camera-dashboard" },
    { id: "dao.benchmarking", path: "/benchmarking" },
    { id: "dao.plant-comparison", path: "/plant-comparison" },
  ],
  "meq-probe": [
    { id: "probe.lamb-summary", path: "/summary-of-scans-lamb" },
    { id: "probe.beef-summary", path: "/summary-of-scans-beef" },
    { id: "probe.shift-scores", path: "/shift-scores" },
  ],
  "meq-camera": [
    { id: "meq-camera", path: "/" },
    { id: "camera.user-analytics", path: "/user-analytics" },
    { id: "camera.grader-scorecard", path: "/grader-scorecard" },
    { id: "camera.mb-gateway-movement", path: "/mb-gateway-movement" },
    { id: "camera.beef-analytics", path: "/beef-analytics" },
    { id: "camera.aus-meat-omm", path: "/omm" },
    { id: "camera.image-comparison", path: "/image-comparison" },
    { id: "camera.wagyu-demo", path: "/wagyu" },
  ],
  management: [
    { id: "management.users", path: "/" },
    { id: "management.gmp-pics", path: "/pics" },
  ],
} as const satisfies Record<string, readonly TabEntry[]>;

/**
 * The Administration tab with no registry id of its own: it shows to whoever
 * holds the permissions edit action, once access is enforced, right after the
 * Users tab (last when Users is hidden).
 */
export const ADMINISTRATION_ACCESS_TAB = {
  path: "/access",
  after: "administration.users",
} as const;

export type TabSection = keyof typeof TAB_MANIFEST;

export type TabId<S extends TabSection> =
  (typeof TAB_MANIFEST)[S][number]["id"];

/**
 * Registry ids of sections and actions the UI tests for membership outside the
 * sidebar and tab bars.
 */
export const IN_PAGE_IDS = {
  datasetTagging: "camera.dataset-tagging",
  ommSettingsEdit: "camera.aus-meat-omm.settings.edit",
  organisationsEdit: "administration.organisations.edit",
  orgLocationsEdit: "administration.org-locations.edit",
  usersEdit: "administration.users.edit",
  permissionsEdit: "administration.permissions.edit",
} as const;

// Index-route matches interpolate with a trailing slash ("/meq-live/") while
// history paths don't — normalize before comparing.
const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;

/** The manifest entry a pathname belongs to, whatever the caller can see. */
export function navEntryFor(pathname: string): NavEntry | undefined {
  const path = stripTrailingSlash(pathname);
  return NAV_MANIFEST.find(
    (entry) => path === entry.to || path.startsWith(`${entry.to}/`),
  );
}

/**
 * The nav page a pathname belongs to: the manifest entry whose `to` is the
 * pathname or a prefix of it ("/meq-live/summary-of-scans" → "/meq-live"), so
 * a section's tabs all resolve to the section. Paths outside the manifest
 * (/main, /no-packages, the auth routes) fall back to their first segment.
 */
export function navPageFor(pathname: string): string {
  const entry = navEntryFor(pathname);
  if (entry) return entry.to;
  const path = stripTrailingSlash(pathname);
  return `/${path.split("/")[1] ?? ""}`;
}

/**
 * True when a navigation leaves one nav page for another — the moment a tab
 * can reload itself onto a newer build without pulling the floor out from
 * under a page the user is still on. Tab switches inside a section and
 * search-only changes stay on the same nav page and return false.
 */
export function isNavPageChange(
  fromPathname: string,
  toPathname: string,
): boolean {
  return navPageFor(fromPathname) !== navPageFor(toPathname);
}
